import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { toast } from 'react-toastify'

import { requestResetPassword } from '~features/reset-password/api'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '~in-app/components'
import { forgotPasswordValidation } from '~in-app/validation'

export const Route = createFileRoute('/(public)/_public/forgot-password')({
  component: ForgotPasswordPage,
  staticData: {
    privileges: [],
  },
})

function ForgotPasswordPage() {
  const navigate = useNavigate()
  const requestResetPasswordMutation = useMutation({
    mutationFn: requestResetPassword,
    onSuccess: () => {
      toast.success('Check your email to reset your password', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      navigate({ to: '/sign-in' })
    },
    onError: (error: Error) => {
      console.error('Login failed:', error)
      toast.error('Invalid username', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    },
  })
  const formOpts = formOptions({
    defaultValues: {
      username: '',
    },
    validatorAdapter: zodValidator(),
    onSubmit: async ({ value }) => {
      requestResetPasswordMutation.mutate(value)
    },
  })
  const formControl = useForm(formOpts)

  return (
    <div className="relative z-10 flex flex-col gap-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Forgot Password?</CardTitle>

          <CardDescription>
            Enter the username associated with your account and we will send you a link to reset your password.
          </CardDescription>
        </CardHeader>

        <form
          noValidate
          onSubmit={
            (e) => {
              e.preventDefault()
              formControl.handleSubmit()
            }
          }
        >
          <CardContent className="grid gap-4">
            <div className="grid gap-2">
              <Label
                required
                htmlFor="email"
              >
                Username
              </Label>

              <formControl.Field
                name="username"
                children={
                  (field) => {
                    return (
                      <>
                        <Input
                          id="username"
                          type="text"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={e => field.handleChange(e.target.value)}
                        />

                        {
                          field.state.meta.errors
                            ? (
                                <em
                                  className="error-message"
                                  role="alert"
                                >
                                  {field.state.meta.errors.join(', ')}
                                </em>
                              )
                            : null
                        }
                      </>

                    )
                  }
                }
                validators={
                  {
                    onChange: forgotPasswordValidation.shape.username,
                  }
                }
              />
            </div>
          </CardContent>

          <CardFooter>
            <Button className="w-full">Send Verification Email</Button>
          </CardFooter>
        </form>
      </Card>

      <Link
        className="flex items-center justify-center gap-2 text-sm hover:underline"
        to="/sign-in"
      >
        <Icon
          height="16"
          icon="lucide:arrow-left"
          width="16"
        />
        Back to sign in
      </Link>
    </div>
  )
}
